import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ArrowUpRight, ChevronDown, Facebook, Instagram, Linkedin, Mail, Menu, MessageCircle, Phone, X, Youtube } from "lucide-react";
import { EMAIL, HOURS, IMG, LOCATION, NAV, PHONE, WHATSAPP } from "../data/content";
import { useContent } from "../context/ContentContext";

const SOCIALS = [
  { label: "LinkedIn", icon: Linkedin, href: "https://in.linkedin.com/company/dishan-international-group" },
  { label: "Instagram", icon: Instagram, href: "https://www.instagram.com/dishaninternationalgroup" },
  { label: "Facebook", icon: Facebook, href: "https://www.facebook.com/profile.php?id=100092318862715" },
  { label: "YouTube", icon: Youtube, href: "https://www.youtube.com/@DishanInternationalGroup" },
];

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location]);

  return (
    <header className={`site-header${scrolled ? " scrolled" : ""}`} data-testid="site-header">
      <div className="header-inner">
        <Link to="/" className="brand" data-testid="brand-home-link">
          <img src={IMG.logo} alt="Dishan International Group" />
          <span className="brand-pipe" aria-hidden="true" />
          <span className="brand-tag">Think positive.<b>Be realistic.</b></span>
        </Link>
        <nav className={`main-nav${open ? " open" : ""}`} aria-label="Primary">
          {NAV.map((item) => (
            <div className={`nav-item${item.children ? " has-drop" : ""}`} key={item.label}>
              <NavLink to={item.path} className={({ isActive }) => (isActive ? "nav-link active" : "nav-link")} data-testid={`nav-${item.label.toLowerCase()}-link`}>
                {item.label}
                {item.children && <ChevronDown size={14} className="chev" />}
              </NavLink>
              {item.children && (
                <div className="dropdown" role="menu">
                  {item.children.map((c) => (
                    <Link to={c.path} key={c.label} className="drop-link" data-testid={`dropdown-${c.label.toLowerCase().replace(/[^a-z]+/g, "-")}-link`}>
                      <span className="drop-title">{c.label}</span>
                      <span className="drop-note">{c.note}</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}
          <Link to="/contact" className="btn btn-primary mobile-cta" data-testid="mobile-consultation-button">Book a Consultation <ArrowUpRight size={15} /></Link>
        </nav>
        <Link to="/contact" className="btn btn-primary header-cta" data-testid="header-consultation-button">Book a Consultation <ArrowUpRight size={15} /></Link>
        <button className="menu-button" onClick={() => setOpen(!open)} aria-label="Toggle navigation" data-testid="mobile-menu-button">
          {open ? <X /> : <Menu />}
        </button>
      </div>
    </header>
  );
};

export const FloatingWhatsApp = () => (
  <a
    href={WHATSAPP} target="_blank" rel="noreferrer"
    className="floating-whatsapp" aria-label={`Chat on WhatsApp: ${PHONE}`}
    data-testid="floating-whatsapp-button"
  >
    <MessageCircle size={24} />
  </a>
);

export const Footer = () => {
  const { c } = useContent();
  return (
  <footer className="footer">
    <div className="container footer-main">
      <div className="footer-brand">
        <img className="footer-logo" src={IMG.logo} alt="Dishan International Group logo" />
        <p className="footer-motto">Think positive. <em>Be realistic.</em></p>
        <p className="footer-note">{c("footer.brand.note", "Strategic Business, Brand & Human Potential Solutions")}</p>
        <div className="socials">
          {SOCIALS.map((s) => (
            <a key={s.label} href={s.href} target="_blank" rel="noreferrer" aria-label={s.label} data-testid={`footer-${s.label.toLowerCase()}-link`}>
              <s.icon size={17} />
            </a>
          ))}
        </div>
      </div>
      <div className="footer-col">
        <span className="eyebrow on-dark">Company</span>
        <Link to="/about" data-testid="footer-about-link">About the group</Link>
        <Link to="/founder" data-testid="footer-founder-link">Founder profile</Link>
        <Link to="/insights" data-testid="footer-insights-link">Insights</Link>
        <Link to="/privacy" data-testid="footer-privacy-link">Privacy policy</Link>
      </div>
      <div className="footer-col">
        <span className="eyebrow on-dark">Work with us</span>
        <Link to="/services" data-testid="footer-services-link">Services</Link>
        <Link to="/education" data-testid="footer-education-link">Education & programs</Link>
        <Link to="/career" data-testid="footer-career-link">Career</Link>
        <Link to="/internship" data-testid="footer-internship-link">Internship</Link>
      </div>
      <div className="footer-col">
        <span className="eyebrow on-dark">Connect</span>
        <a href={`mailto:${EMAIL}`} className="footer-email" data-testid="footer-email-link"><Mail size={15} /> {EMAIL}</a>
        <a href={WHATSAPP} target="_blank" rel="noreferrer" className="footer-email" data-testid="footer-whatsapp-link"><Phone size={15} /> {PHONE}</a>
        <span className="footer-meta">{HOURS}</span>
        <span className="footer-meta">{LOCATION}</span>
        <Link to="/contact" data-testid="footer-contact-link">Book a Consultation</Link>
      </div>
    </div>
    <div className="container footer-bottom">
      <span>© {new Date().getFullYear()} Dishan International Group. All rights reserved.</span>
      <span><Link to="/privacy" data-testid="footer-bottom-privacy-link">Privacy & Policy</Link> · Terms · Disclaimer</span>
    </div>
  </footer>
  );
};
